import {
  Box,
  Button,
  Dialog,
  IconButton,
  Typography,
  ToggleButtonGroup,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  type AssistantModalTheme,
  modalThemeTokens,
  buildModalCssVars,
} from '../AssistantEditorModals/theme';

const StyledDialog = styled(Dialog)({
  '& .MuiBackdrop-root': { backgroundColor: 'var(--am-backdrop)' },
  '& .MuiDialog-paper': {
    borderRadius: '16px',
    padding: 0,
    boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.12)',
    background: 'var(--am-paper-bg)',
  },
});

const ModalContent = styled(Box)({
  background: 'var(--am-paper-bg)',
  borderRadius: '16px',
  width: '100%',
  position: 'relative',
});

const ModalHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(3),
}));

const ModalBody = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

const ModalActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1.5),
  justifyContent: 'flex-end',
  marginTop: theme.spacing(1),
}));

export const CloseButton = styled(IconButton)({
  width: '32px',
  height: '32px',
  color: 'var(--am-close-color)',
  padding: '4px',
  '&:hover': {
    backgroundColor: 'var(--am-surface-2)',
    color: 'var(--am-text-primary)',
  },
});

export const ModalTitle = styled(Typography)({
  fontSize: '20px',
  fontWeight: 600,
  color: 'var(--am-text-primary)',
}) as typeof Typography;

export const PrimaryButton = styled(Button)({
  height: '48px',
  borderRadius: '8px',
  fontSize: '16px',
  fontWeight: 500,
  lineHeight: '24px',
  textTransform: 'none',
  color: 'var(--am-primary-btn-color) !important',
  backgroundColor: 'var(--am-primary-btn-bg)',
  padding: '12px 24px',
  minWidth: '140px',
  border: 'none',
  '&:hover': { backgroundColor: 'var(--am-primary-btn-hover-bg)' },
  '&:disabled': {
    backgroundColor: 'var(--am-border-mid)',
    color: 'var(--am-text-secondary)',
  },
});

export const CancelButton = styled(Button)({
  height: '48px',
  borderRadius: '8px',
  fontSize: '16px',
  fontWeight: 500,
  lineHeight: '24px',
  textTransform: 'none',
  color: 'var(--am-text-primary)',
  backgroundColor: 'transparent',
  border: '2px solid var(--am-cancel-border)',
  padding: '12px 24px',
  minWidth: '140px',
  '&:hover': {
    backgroundColor: 'var(--am-cancel-hover-bg)',
    border: '2px solid var(--am-cancel-hover-border)',
  },
  '&:disabled': { opacity: 0.5 },
});

export const ScriptsDialog = styled(StyledDialog, {
  shouldForwardProp: (prop) => prop !== '$theme',
})<{ $theme?: AssistantModalTheme }>(({ $theme = 'light' }) => {
  const tokens = modalThemeTokens[$theme];
  const cssVars = buildModalCssVars(tokens);
  return {
    // Set CSS variables on the dialog root so they cascade to paper + all children
    ...(cssVars as any),
    // Override background CSS vars to be semi-transparent only in dark theme (glass effect)
    ...($theme === 'dark' && {
      '--am-paper-bg':
        'linear-gradient(135deg, rgba(28, 28, 34, 0.88) 0%, rgba(16, 16, 20, 0.88) 100%)',
      '--am-header-bg': 'rgba(15, 17, 23, 0.75)',
      '--am-body-bg':
        'linear-gradient(135deg, rgba(28, 28, 34, 0.88) 0%, rgba(16, 16, 20, 0.88) 100%)',
    } as any),
    '& .MuiBackdrop-root': { backgroundColor: tokens.backdrop },
    '& .MuiDialog-paper': {
      width: '100%',
      maxWidth: 800,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      borderRadius: 16,
      background: tokens.paperBg,
      ...($theme === 'dark' && {
        background:
          'linear-gradient(135deg, rgba(28, 28, 34, 0.88) 0%, rgba(16, 16, 20, 0.88) 100%)',
        backdropFilter: 'blur(18px)',
        WebkitBackdropFilter: 'blur(18px)',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        boxShadow: '0 18px 50px rgba(0, 0, 0, 0.45)',
      }),
      // MUI form element overrides so Select/inputs respect the theme
      '& .MuiInputBase-root': {
        color: 'var(--am-text-primary)',
        backgroundColor: 'var(--am-input-bg)',
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'var(--am-border-mid)',
      },
      '& .MuiSelect-icon': {
        color: 'var(--am-text-secondary)',
      },
      '& .MuiDivider-root': {
        borderColor: 'var(--am-border)',
      },
      ...($theme === 'dark' && {
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: 'var(--am-focus-border-color)',
        },
        '& .MuiRadio-root': { color: 'var(--am-focus-border-color)' },
        '& .MuiCheckbox-root': { color: 'var(--am-focus-border-color)' },
      } as any),
    },
    '& .MuiBox-root': { maxWidth: '100%' },
  };
});

export const ScriptsModalContent = styled(ModalContent)({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  overflow: 'hidden',
  maxWidth: '100%',
  borderRadius: 16,
  boxShadow: 'none',
});

export const ScriptsModalHeader = styled(ModalHeader)({
  padding: '16px 24px',
  position: 'sticky',
  top: -18,
  zIndex: 1,
  background: 'var(--am-header-bg)',
  flexShrink: 0,
  borderBottom: '1px solid var(--am-border)',
});

export const HeaderTitleRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
});

export const HeaderActionsRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 16,
});

export const UnsavedChangesRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
});

export const UnsavedChangesDot = styled(Box)(({ theme }) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: theme.palette.warning.main,
  flexShrink: 0,
}));

export const UnsavedChangesText = styled(Typography)(({ theme }) => ({
  fontSize: '13px',
  color: theme.palette.warning.main,
  fontWeight: 500,
})) as typeof Typography;

export const ScriptsModalBody = styled(ModalBody)({
  padding: '16px 24px 24px',
  gap: 24,
  overflowY: 'auto',
  flex: 1,
  background: 'var(--am-body-bg)',
});

export const ScriptsModalActions = styled(ModalActions)({
  justifyContent: 'space-between',
  flexShrink: 0,
  padding: '16px 24px',
  borderTop: '1px solid var(--am-border)',
  background: 'var(--am-header-bg)',
});

export const StepsHeaderRow = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  marginBottom: 16,
  marginTop: 16,
});

export const StepsTitle = styled(Typography)({
  fontWeight: 600,
  fontSize: '15px',
  color: 'var(--am-text-primary)',
}) as typeof Typography;

export const AddStepButton = styled(Button)({
  fontSize: '13px',
  textTransform: 'none',
  borderRadius: 4,
  padding: '0 12px',
  height: 32,
  minHeight: 'unset',
  whiteSpace: 'nowrap',
  color: 'var(--am-ghost-btn-color)',
  backgroundColor: 'transparent',
  border: 'var(--am-ghost-btn-border)',
  '&:hover': {
    backgroundColor: 'var(--am-ghost-btn-hover-bg)',
    border: 'var(--am-ghost-btn-border)',
  },
});

export const FixedStepsBar = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$theme',
})<{ $theme?: AssistantModalTheme }>(({ $theme }) => ({
  position: 'sticky',
  top: -16,
  zIndex: 10,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  background: 'var(--am-header-bg)',
  borderBottom: '1px solid var(--am-border)',
  padding: '8px 24px',
  marginLeft: -24,
  marginRight: -24,
  width: 'calc(100% + 48px)',
  maxWidth: 'calc(100% + 48px) !important',
  ...($theme === 'dark' && {
    backdropFilter: 'blur(18px)',
    WebkitBackdropFilter: 'blur(18px)',
  }),
}));

export const StepsContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 20,
});

export const ScriptToggleButtonGroup = styled(ToggleButtonGroup)({
  gap: 6,
  '& .MuiToggleButtonGroup-grouped': {
    border: '0 !important',
    borderRadius: '6px !important',
  },
  '& .MuiToggleButton-root': {
    fontSize: 'var(--am-toggle-font-size)',
    textTransform: 'none',
    padding: 'var(--am-toggle-padding)',
    borderRadius: '6px !important',
    border: 'var(--am-toggle-border) !important',
    backgroundColor: 'var(--am-toggle-bg)',
    color: 'var(--am-toggle-color)',
    fontWeight: 400,
    transition: 'all 0.12s',
    '&.Mui-selected': {
      color: 'var(--am-toggle-active-color)',
      fontWeight: 'var(--am-toggle-active-weight)' as any,
      backgroundColor: 'var(--am-toggle-active-bg)',
      border: 'var(--am-toggle-active-border) !important',
    },
    '&.Mui-selected:hover': {
      backgroundColor: 'var(--am-toggle-active-bg)',
    },
  },
});

export const SectionLabelText = styled(Typography)({
  fontSize: '11px',
  fontWeight: 600,
  color: 'var(--am-label-color)',
  letterSpacing: '0.07em',
  textTransform: 'uppercase',
  marginBottom: 8,
}) as typeof Typography;


export const LanguageTab = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active' && prop !== 'complete',
})<{ active?: boolean; complete?: boolean }>(({ active, complete }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '4px 10px',
  borderRadius: 6,
  // "complete" (translated) chip color/border comes from
  // --am-lang-complete-*, which is theme-aware: light keeps the original
  // green chip, dark reads as normal text/border — only the checkmark badge
  // inside it (LangCompleteBadge, via --am-success-color) stays green in
  // dark mode.
  border: active
    ? 'var(--am-tab-active-border)'
    : complete
    ? '1.5px solid var(--am-lang-complete-border-color)'
    : '1.5px solid var(--am-border-mid)',
  backgroundColor: active
    ? 'var(--am-tab-active-bg)'
    : complete
    ? 'var(--am-lang-complete-bg)'
    : 'transparent',
  cursor: 'pointer',
  userSelect: 'none' as const,
  fontSize: '12px',
  fontWeight: active ? 'var(--am-lang-tab-active-weight)' : 400,
  color: active
    ? 'var(--am-tab-active-color)'
    : complete
    ? 'var(--am-lang-complete-text-color)'
    : 'var(--am-text-primary)',
  transition: 'all 0.15s',
  '&:hover': {
    border: active
      ? 'var(--am-tab-active-border)'
      : complete
      ? '1.5px solid var(--am-lang-complete-border-color)'
      : '1.5px solid var(--am-border-heavy)',
  },
}));
